/**
 * Broker Portal tracking utilities.
 * This file consolidates all Datadog tracking logic for the broker portal,
 * including both generic tracking functions and broker-specific event wrappers.
 */
import {
  loginEvent as baseLoginEvent,
  clearUser,
  loginMetrics,
  logoutEvent,
  setAccount,
  setGlobalContext,
  submitEvent,
  initDatadog,
} from "@platform/browser-logger";

import envVariables from "../projectEnvVariables";

import { isProduction } from "./environment";

export interface BrokerInfo {
  name: string;
  type?: string;
  externalId?: string | null;
  sfExternalId?: string | null;
}

/**
 * Environment variables required for Datadog tracking initialization.
 */
interface DatadogEnvVariables {
  VITE_REACT_APP_ENV: string;
  VITE_REACT_APP_METRICS_APP_ID: string;
  VITE_REACT_APP_METRICS_CLIENT_TOKEN: string;
  VITE_REACT_APP_METRICS_SITE: string;
  VITE_REACT_APP_METRICS_SERVICE: string;
  VITE_REACT_APP_VERSION: string;
}

const userTypeMap = {
  admin: "Admin",
  analyst: "Analyst",
  broker: "Broker Super User",
  agent: "Broker Regular User",
} as const;

export type BrokerPortalUserType = keyof typeof userTypeMap;

/**
 * Initializes Datadog tracking for the broker portal.
 * Only initializes in production environments.
 *
 * @example
 * ```typescript
 * // Call once during app initialization
 * initBrokerPortalTracking();
 * ```
 */
export function initBrokerPortalTracking(): void {
  if (!isProduction()) {
    return;
  }

  const vars = envVariables as DatadogEnvVariables;

  initDatadog({
    env: vars.VITE_REACT_APP_ENV,
    applicationId: vars.VITE_REACT_APP_METRICS_APP_ID,
    clientToken: vars.VITE_REACT_APP_METRICS_CLIENT_TOKEN,
    site: vars.VITE_REACT_APP_METRICS_SITE,
    service: vars.VITE_REACT_APP_METRICS_SERVICE,
    version: vars.VITE_REACT_APP_VERSION,
  });
}

/**
 * Helper function to format user type from internal codes to display names.
 *
 * @param userType - Internal user type code
 * @returns Formatted user type name or null if unknown
 */
function getUserType(userType: BrokerPortalUserType | string): string | null {
  const key = userType.toLowerCase() as BrokerPortalUserType;
  return userTypeMap[key] ?? null;
}

/**
 * Prefers given + family name because Auth0 defaults `name` to the email for
 * username/password accounts.
 */
export const getDisplayName = (claims: {
  name?: string;
  given_name?: string;
  family_name?: string;
}): string => {
  if (claims.given_name && claims.family_name) {
    return `${claims.given_name} ${claims.family_name}`;
  }
  return claims.name ?? "";
};

/**
 * Tracks a user login event with broker-portal-specific user type formatting.
 * Sets the user context in Datadog and tracks a login action.
 *
 * @param name - User's full name
 * @param email - User's email address
 * @param userType - Internal user type code
 * @example
 * ```typescript
 * loginEvent('John Doe', 'john@example.com', 'broker');
 * ```
 */
export function loginEvent(
  name: string,
  email: string,
  userType: BrokerPortalUserType | string,
): void {
  const formattedUserType = getUserType(userType);
  baseLoginEvent({
    name,
    email,
    userType: formattedUserType || userType,
  });
}

/**
 * Sets broker group context that will be included in all subsequent events.
 * Adds broker name and type to the global context.
 *
 * @param brokerInfo - Broker information object
 * @example
 * ```typescript
 * setUserBrokerGroups({
 *   name: 'Acme Insurance',
 *   type: 'wholesale'
 * });
 * ```
 */
export function setUserBrokerGroups(brokerInfo: BrokerInfo): void {
  if (brokerInfo) {
    setGlobalContext("broker_name", brokerInfo.name);
    setGlobalContext("broker_type", brokerInfo.type);
    const accountId = brokerInfo.sfExternalId ?? brokerInfo.externalId;
    if (accountId) {
      setAccount(accountId, brokerInfo.name);
    }
  }
}

/**
 * Sets Datadog account context for internal roles (admin, analyst, etc.).
 * Uses the role key as the stable account_id and the display name as account_name.
 * Roles not in userTypeMap fall back to title-cased key (e.g. "consultant" → "Consultant").
 */
export function setInternalUserAccount(userType: string): void {
  const key = userType.toLowerCase();
  if (key) {
    const displayName =
      getUserType(key) ?? key.charAt(0).toUpperCase() + key.slice(1);
    setAccount(key, displayName);
  }
}

export { clearUser, loginMetrics, logoutEvent, setAccount, submitEvent };
